/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-use-before-define */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';
import { formatRFC3339, format } from 'date-fns';
import ApiService from '@services/api';
import { useSession } from '@hooks/useSession';
import INFe from '@interfaces/INFe';
import {
  MdDownload,
  MdRemoveRedEye,
  MdContentCopy,
  MdPrint,
  MdEmail,
} from 'react-icons/md';
import { StatusColor } from '@styles/colors';

import { downloadFile } from '@utils/download';
import Loading from '@components/Loading';
import RoundButton from '@components/RoundButton';
import Modal from '@components/Modal';
import NotAuthenticated from '@components/NotAuthenticated';
import SelectInput from '@components/SelectInput';
import Input from '@components/Input';
import Button from '@components/Button';
import Paginator from '@components/Paginator';
import settingsStore from '@store/SettingsStore';
import SendEmailDialog from '@components/SendEmailDialog';

import {
  Container,
  Title,
  Selection,
  Row,
  Field,
  Buttons,
  DateInput,
  Table,
  ContainerEmissao,
  WrapperStatus,
  ContainerStatus,
  Status,
  Actions,
} from './styles';

const currencyFormat = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const NFeDocs: React.FC = observer(() => {
  const params = useParams();
  const { isMigrating } = useSession();
  const { api } = new ApiService(params.token);

  const modeloOptions = useMemo(() => {
    return [
      { label: 'Todos', value: 1 },
      { label: '55 (NF-e)', value: 55 },
      { label: '65 (NFC-e)', value: 65 },
      { label: 'NS (NFS-e)', value: 'NS' },
    ].filter((option) => {
      if (option.value === 1) return true;
      return settingsStore.models.includes(option.value.toString());
    });
  }, [settingsStore.models]);

  const [dataIniSel, setDataIniSel] = useState(
    formatRFC3339(new Date()).substr(0, 10)
  );
  const [dataFimSel, setDataFimSel] = useState(
    formatRFC3339(new Date()).substr(0, 10)
  );
  const [modeloSel, setModeloSel] = useState(modeloOptions[0]);
  const [serieSel, setSerieSel] = useState('');
  const [nroIniSel, setNroIniSel] = useState('');
  const [nroFimSel, setNroFimSel] = useState('');

  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [rejeicao, setRejeicao] = useState('');
  const [notas, setNotas] = useState<INFe[]>([]);
  const [page, setPage] = useState(1);
  const [totalNotas, setTotalNotas] = useState(100);
  const [queryCount, setQueryCount] = useState(0);
  const [emailModalData, setEmailModalData] = useState<{
    nota: INFe;
    email: string;
  } | null>(null);

  useEffect(() => {
    if (!isMigrating) {
      loadNotas();
    }
  }, [page, isMigrating]);

  async function loadNotas() {
    try {
      setLoading(true);

      const queryParams: any = {
        page,
        dateStart: dataIniSel,
        dateEnd: dataFimSel,
      };
      if (modeloSel.value !== 1) {
        queryParams.model = modeloSel.value;
      }
      if (serieSel) {
        queryParams.serie = serieSel;
      }
      if (nroIniSel) {
        queryParams.numberStart = nroIniSel;
      }
      if (nroFimSel) {
        queryParams.numberEnd = nroFimSel;
      }

      const resp = await api.get('/nfe/docs', {
        params: queryParams,
      });
      setNotas(resp.data.items);
      setTotalNotas(resp.data.total);
      setQueryCount(resp.data.queryCount);
      setAuthenticated(true);
    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setAuthenticated(false);
      } else {
        console.log(error.stack);
        toast.error('Falha ao carregar');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(nota: INFe) {
    try {
      const resp = await api.get(`/nfe/docs/xml/${nota.id}`, {
        responseType: 'blob',
      });
      if (nota.statusId === 101) {
        const dh = format(new Date(), 'yyyyMMdd-HHmmss');
        downloadFile(resp.data, `xml-${dh}.zip`);
      } else if (nota.model === 'NS') {
        downloadFile(resp.data, `${nota.protocol}-nfse.xml`);
      } else {
        downloadFile(
          resp.data,
          `${nota.protocol}_v${nota.version}-procNFe.xml`
        );
      }
      // const urlObj = window.URL.createObjectURL(resp.data);
      // window.open(urlObj, '_blank')?.focus();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('Download indisponível');
      } else {
        toast.error('Falha ao realizar o download');
      }
    }
  }

  async function handlePDF(nota: INFe) {
    try {
      const dfeModels = {
        '55': 'nfe',
        '65': 'nfce',
        NS: 'nfse',
      } as const;
      const dfeTypeStr = dfeModels[nota.model as keyof typeof dfeModels];
      const resp = await api.get(`/pdf/${dfeTypeStr}/${nota.key}`, {
        responseType: 'blob',
      });
      const urlObj = window.URL.createObjectURL(resp.data);
      window.open(urlObj, '_blank')?.focus();
    } catch (error: any) {
      if (error?.response?.status === 404) {
        toast.error('PDF indisponível');
      } else {
        toast.error('Falha ao gerar o PDF');
      }
    }
  }

  async function handleEmail(nota: INFe, recipientEmail: string) {
    try {
      let typeDoc = 'nfe';
      if (nota.model === '65') {
        typeDoc = 'nfce';
      } else if (nota.model === 'NS') {
        typeDoc = 'nfse';
      }
      await api.post(`/email/${typeDoc}/${nota.key}`, {
        email: recipientEmail,
      });
      toast.success('E-mail agendado com sucesso');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Falha ao enviar E-mail');
    }
  }

  async function handleCopyClipboard(key: string) {
    try {
      const textField = document.createElement('textarea');
      textField.innerText = key;
      document.body.appendChild(textField);
      textField.select();
      document.execCommand('copy');
      textField.remove();
      toast.success('Chave copiada para a área de transferência');
    } catch (error) {
      toast.error('Falha ao copiar a chave de acesso');
    }
  }

  function renderEmissao(nota: INFe) {
    return (
      <ContainerEmissao>
        {format(new Date(nota.issueDate), 'dd/MM/yyyy HH:mm')}
        {nota.contingencyType && (
          <Status style={{ background: '#0071FF' }}>
            {nota.contingencyType === 9 && 'Contingência Offline'}
            {nota.contingencyType === 4 && 'Contingência EPEC'}
            {nota.contingencyType === 2 && 'Contingência FS-IA'}
            {nota.contingencyType === 5 && 'Contingência FS-DA'}
            {nota.contingencyType === 6 && 'Contingência SVC-AN'}
            {nota.contingencyType === 7 && 'Contingência SVC-RS'}
          </Status>
        )}
      </ContainerEmissao>
    );
  }

  function renderStatus(cStat: number, xStat: string) {
    if (cStat === 100)
      return (
        <Status style={{ background: StatusColor.completed }}>
          Autorizada
        </Status>
      );
    if (cStat === 150)
      return (
        <Status style={{ background: StatusColor.completed }}>
          Autorizada - Fora do prazo
        </Status>
      );
    if (cStat === 101)
      return (
        <Status style={{ background: StatusColor.cancelled }}>Cancelada</Status>
      );
    if (cStat === 151)
      return (
        <Status style={{ background: StatusColor.cancelled }}>
          Cancelada - Fora do prazo
        </Status>
      );
    if (cStat === 110)
      return (
        <Status style={{ background: StatusColor.pending }}>Denegada</Status>
      );
    if (cStat > 200)
      return (
        <>
          <Status style={{ background: StatusColor.failed }}>Rejeitada</Status>{' '}
          <RoundButton
            hint="Visualizar Rejeição"
            onClick={() => setRejeicao(xStat)}
          >
            <MdRemoveRedEye size={18} />
          </RoundButton>
        </>
      );
    return xStat;
  }

  if (loading)
    return (
      <Container>
        <Loading />
      </Container>
    );

  if (!authenticated) {
    return <NotAuthenticated />;
  }

  return (
    <Container>
      <Title>NOTAS FISCAIS</Title>
      <Selection>
        <Row>
          <div style={{ marginRight: 10 }}>
            <Field>
              Emissão
              <div>
                <DateInput
                  value={dataIniSel}
                  onChange={(e: any) => setDataIniSel(e.target.value)}
                />{' '}
                a{' '}
                <DateInput
                  value={dataFimSel}
                  onChange={(e: any) => setDataFimSel(e.target.value)}
                />
              </div>
            </Field>
          </div>
          <Field>
            Modelo
            <SelectInput
              fullWidth
              options={modeloOptions}
              defaultValue={modeloOptions[0]}
              value={modeloSel}
              onChange={(newValue: any) => setModeloSel(newValue)}
            />
          </Field>
          <Field>
            Série
            <Input
              onlyNumber
              value={serieSel}
              onChange={(e: any) => {
                setSerieSel(e.target.value);
              }}
            />
          </Field>
          <Field>
            Número Inicial
            <Input
              onlyNumber
              value={nroIniSel}
              onChange={(e: any) => setNroIniSel(e.target.value)}
            />
          </Field>
          <Field>
            Número Final
            <Input
              onlyNumber
              value={nroFimSel}
              onChange={(e: any) => setNroFimSel(e.target.value)}
            />
          </Field>
        </Row>

        <Buttons>
          <Button
            onClick={() => {
              setPage(1);
              loadNotas();
            }}
          >
            Consultar
          </Button>
        </Buttons>
      </Selection>

      {notas.length > 0 && (
        <>
          <Table>
            <thead>
              <tr>
                <th style={{ width: '10%' }}>Número</th>
                <th style={{ width: '8%' }}>Série</th>
                <th style={{ width: '5%' }}>Modelo</th>
                <th style={{ width: '20%' }}>Emissão</th>
                <th style={{ width: '20%' }}>Destinatário</th>
                <th style={{ width: '10%' }}>Valor</th>
                <th style={{ width: '20%' }}>Status</th>
                <th style={{ width: '7%' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {notas.map((nota) => (
                <tr key={nota.id}>
                  <td>{nota.number}</td>
                  <td>{nota.serie}</td>
                  <td>{nota.model}</td>
                  <td>{renderEmissao(nota)}</td>
                  <td>{nota?.receiver?.name}</td>
                  <td>{nota.total ? currencyFormat.format(nota.total) : ''}</td>
                  <td>
                    <WrapperStatus>
                      <ContainerStatus>
                        {nota.receiptDate &&
                          format(
                            new Date(nota.receiptDate),
                            'dd/MM/yyyy HH:mm'
                          )}
                        {renderStatus(nota.statusId, nota.status)}
                      </ContainerStatus>
                    </WrapperStatus>
                  </td>
                  <td>
                    <Actions>
                      <RoundButton
                        hint="Copiar Chave de Acesso"
                        onClick={() => handleCopyClipboard(nota.key)}
                      >
                        <MdContentCopy size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Download"
                        onClick={() => handleDownload(nota)}
                      >
                        <MdDownload size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Gerar PDF"
                        onClick={() => handlePDF(nota)}
                      >
                        <MdPrint size={18} />
                      </RoundButton>
                      <RoundButton
                        hint="Enviar E-mail"
                        onClick={() =>
                          setEmailModalData({
                            nota,
                            email: (nota as any).receiver?.email || '',
                          })
                        }
                      >
                        <MdEmail size={18} />
                      </RoundButton>
                    </Actions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div>
            Exibindo {page > 1 ? (page - 1) * 20 + 1 : 1} a{' '}
            {page > 1 ? (page - 1) * 20 + queryCount : queryCount} de um total
            de {totalNotas} notas.
          </div>
        </>
      )}

      {totalNotas > 20 && (
        <Paginator
          activePage={Number(page)}
          limitPage={20}
          totalRecords={totalNotas}
          onChange={(newPage) => setPage(newPage)}
        />
      )}

      {rejeicao && (
        <Modal onCloseRequest={() => setRejeicao('')} exitButton={false}>
          <div style={{ padding: 40 }}>{rejeicao}</div>
        </Modal>
      )}

      {emailModalData && (
        <SendEmailDialog
          isOpen={!!emailModalData}
          initialEmail={emailModalData.email}
          onConfirm={(email) => {
            handleEmail(emailModalData.nota, email);
            setEmailModalData(null);
          }}
          onCancel={() => setEmailModalData(null)}
        />
      )}
    </Container>
  );
});

export default NFeDocs;
